import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import { Response, SelectItemProps } from '@ui/src';
import {
  FeeItem,
  FeeTypeEnum,
  FeesListItem,
  FeesValidators,
  FeeTransactionTypeEnum,
} from '../../types';
import { feesApi } from '../services';

interface TransactionTypeProps extends SelectItemProps {
  schemaName: string;
}

export interface FeesListState {
  fees: FeesListItem[];
  validators?: FeesValidators;
  transactionTypes?: TransactionTypeProps[];
  defaultFees?: FeeItem[];
  feesList?: { type: string; subType?: string; list?: FeeItem[] }[];
}

const initialState: FeesListState = {
  fees: [],
};

const updateFeesList = ({
  data,
  item,
}: {
  data: FeeItem;
  item: FeesListItem;
}) => {
  let result = { ...item };

  let nextList: FeeItem[] = [];

  if (result.list) {
    nextList = ([] as FeeItem[]).concat(result.list);
  }

  if (data.feeType === result.type) {
    if (data.paymentType || data.regularFeeSubType) {
      if (
        (data.paymentType && data.paymentType === result?.subType) ||
        (data.regularFeeSubType && data.regularFeeSubType === result?.subType)
      ) {
        let feeList: FeeItem[] = [];

        if (result?.list) {
          feeList = ([] as FeeItem[]).concat(result.list);
        }
        if (feeList.find((fee) => fee.id === data.id)) {
          feeList = feeList.map((fee) => {
            let feeResult = { ...fee };

            if (data.id === feeResult.id) {
              feeResult = data;
            }

            return feeResult;
          });
        } else {
          feeList.push(data);
        }

        result = {
          ...result,
          list: feeList,
        };
      }
    } else if (nextList.find((fee) => fee.id === data.id)) {
      nextList = nextList.map((fee) => {
        let feeResult = { ...fee };

        if (data.id === feeResult.id) {
          feeResult = data;
        }

        return feeResult;
      });

      result.list = nextList;
    } else {
      nextList.push(data);
      result.list = nextList;
    }
  } else if (data.feeTransactionType === FeeTransactionTypeEnum.INTERNAL) {
    nextList.push(data);
    result.list = nextList;
  }

  return result;
};

export const feesListSlice = createSlice({
  name: 'feesList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      feesApi.endpoints.getFeesValidators.matchFulfilled,
      (state, action: PayloadAction<{ data: FeesValidators }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      feesApi.endpoints.getConfigurationTransactionTypes.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<{ name: string; id: string }>>,
      ) => {
        state.transactionTypes = action.payload.data.edges.map((item) => ({
          name: item.node.name
            .split('_')
            .map((name) => `${name.charAt(0)}${name.slice(1).toLowerCase()}`)
            .join(' '),
          value: item.node.id,
          schemaName: item.node.name,
        }));
      },
    );
    builder.addMatcher(
      feesApi.endpoints.createFeeItem.matchFulfilled,
      (state, action: PayloadAction<{ data: FeeItem }>) => {
        const { data } = action.payload;

        const nextDefaultFees = ([] as FeeItem[]).concat(
          state.defaultFees as FeeItem[],
        );

        nextDefaultFees.push(data);

        const nextFees = state?.feesList?.map((item) => {
          let result = { ...item };

          const fees = nextDefaultFees
            .filter(
              (fee) =>
                (fee.feeType === result.type &&
                  fee.feeTransactionType !== FeeTransactionTypeEnum.INTERNAL) ||
                (result.type === FeeTypeEnum.INTERNAL &&
                  fee.feeTransactionType === FeeTransactionTypeEnum.INTERNAL),
            )
            .sort((a, b) => a?.name?.localeCompare(b?.name));

          if (fees.length) {
            fees.forEach((fee) => {
              result = updateFeesList({
                item: result,
                data: fee,
              });
            });
          }

          return result;
        });

        state.fees = nextFees as FeesListItem[];
        state.defaultFees = nextDefaultFees;
      },
    );
    builder.addMatcher(
      feesApi.endpoints.updateFeeItem.matchFulfilled,
      (state, action: PayloadAction<{ data: FeeItem }>) => {
        const { data } = action.payload;

        const nextFees = state.fees.map((item) => {
          const result = updateFeesList({
            item,
            data,
          });

          return result;
        });

        state.fees = nextFees;
      },
    );
    builder.addMatcher(
      feesApi.endpoints.getFeesList.matchFulfilled,
      (state, action: PayloadAction<Response<FeeItem>>) => {
        const { edges } = action.payload.data;
        state.defaultFees = edges.map((item) => item.node);

        const feesList: { type: string; subType?: string; list?: FeeItem[] }[] =
          [];

        edges.forEach((item) => {
          const currentFee: {
            type: string;
            subType?: string;
            list?: FeeItem[];
          } = {
            type: item.node.feeType,
          };

          if (item.node.paymentType) {
            currentFee.subType = item.node.paymentType;
          } else if (item.node.regularFeeSubType) {
            currentFee.subType = item.node.regularFeeSubType;
          } else if (item.node.feeTransactionType === 'INTERNAL') {
            currentFee.type = item.node.feeTransactionType;
          }

          if (
            !feesList.find((fee) => {
              const feeItem: {
                type: string;
                subType?: string;
                list?: FeeItem[];
              } = {
                type: fee.type,
              };

              if (fee.subType) {
                feeItem.subType = fee.subType;
              }

              return isEqual(currentFee, feeItem);
            })
          ) {
            feesList.push(currentFee);
          }
        });

        state.feesList = feesList;

        const nextFees = feesList.map((item) => {
          let result = { ...item };

          const fees = edges.filter(
            (fee) =>
              (fee.node.feeType === result.type &&
                fee.node.feeTransactionType !==
                  FeeTransactionTypeEnum.INTERNAL) ||
              (result.type === FeeTypeEnum.INTERNAL &&
                fee.node.feeTransactionType ===
                  FeeTransactionTypeEnum.INTERNAL),
          );

          if (fees.length) {
            fees.forEach((fee) => {
              result = updateFeesList({
                item: result,
                data: fee.node,
              });
            });
          }

          return result;
        });

        state.fees = nextFees;
      },
    );
  },
});

export default feesListSlice.reducer;
